import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import { useId, useMemo } from 'react';
import cx from 'classnames';
import { scaleLinear } from 'd3-scale';
import SyntaxHighlighter, { createElement } from 'react-syntax-highlighter';
import { atomOneDark, atomOneLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { Tooltip } from 'react-tooltip';
import { useParcaContext, useURLState } from '@parca/components';
import { useProfileViewContext } from '../ProfileView/ProfileViewContext';
import { LineNo } from './LineNo';
import { langaugeFromFile } from './lang-detector';
// cannot make this a function on the number as we need the classes to be static for tailwind
const charsToWidthMap = {
    1: 'w-3',
    2: 'w-5',
    3: 'w-7',
    4: 'w-9',
    5: 'w-11',
    6: 'w-[52px]',
    7: 'w-[60px]]',
    8: 'w-[68px]',
    9: 'w-[76px]',
    10: 'w-[84px]',
    11: 'w-[92px]',
    12: 'w-[100px]',
    13: 'w-[108px]',
    14: 'w-[116px]',
};
const intensityScale = scaleLinear().domain([0, 99]).range([0.05, 0.75]);
const LineProfileMetadata = ({ value, total, filtered, }) => {
    const commonClasses = 'w-[52px] shrink-0';
    const id = useId();
    const { sampleUnit } = useProfileViewContext();
    if (value === 0n) {
        return _jsx("div", { className: cx(commonClasses) });
    }
    const unfilteredPercent = (Number(value) / Number(total + filtered)) * 100;
    const filteredPercent = (Number(value) / Number(total)) * 100;
    const valueString = value.toString();
    const valueWithUnit = `${valueString}${sampleUnit}`;
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: cx('w- flex justify-end overflow-hidden text-ellipsis whitespace-nowrap', commonClasses), style: { backgroundColor: `rgba(236, 151, 6, ${intensityScale(unfilteredPercent)})` }, "data-tooltip-id": id, "data-tooltip-content": `${valueWithUnit} (${unfilteredPercent.toFixed(2)}%${filtered > 0n ? ` / ${filteredPercent.toFixed(2)}%` : ''})`, children: valueString }), _jsx(Tooltip, { id: id })] }));
};
const charsToWidth = (chars) => {
    return charsToWidthMap[chars];
};
export const profileAwareRenderer = (cumulative, flat, total, filtered) => {
    return function ProfileAwareRenderer({ rows, stylesheet, useInlineStyles, }) {
        const lineNumberWidth = charsToWidth(rows.length.toString().length);
        const [sourceLine, setSourceLine] = useURLState({ param: 'source_line', navigateTo: () => { } });
        return (_jsx(_Fragment, { children: rows.map((node, i) => {
                const lineNumber = node.children[0].children[0].value;
                const isCurrentLine = sourceLine === lineNumber.toString();
                node.children = node.children.slice(1);
                return (_jsxs("div", { className: "flex gap-1", children: [_jsx("div", { className: cx('shrink-0 overflow-hidden border-r border-r-gray-200 text-right dark:border-r-gray-700', lineNumberWidth), children: _jsx(LineNo, { value: lineNumber, isCurrent: isCurrentLine, setCurrentLine: () => setSourceLine(lineNumber.toString()) }) }), _jsx(LineProfileMetadata, { value: cumulative?.get(i) ?? 0n, total: total, filtered: filtered }), _jsx(LineProfileMetadata, { value: flat?.get(i) ?? 0n, total: total, filtered: filtered }), _jsx("div", { className: cx('w-full flex-grow-0 border-l border-gray-200 pl-1 dark:border-gray-700', {
                                'bg-yellow-200 dark:bg-yellow-700': isCurrentLine,
                            }), children: createElement({
                                key: `source-line-${i}`,
                                node,
                                stylesheet,
                                useInlineStyles,
                            }) })] }, `${i}`));
            }) }));
    };
};
export const Highlighter = ({ file, content, renderer }) => {
    const { isDarkMode } = useParcaContext();
    const language = useMemo(() => langaugeFromFile(file), [file]);
    return (_jsxs("div", { className: "relative", children: [_jsxs("div", { className: "flex gap-2 text-xs", children: [_jsx("div", { className: cx('text-right', charsToWidth(content
                            .split(
                        // prettier-ignore
                        '\n')
                            .length.toString().length)), children: "Line" }), _jsxs("div", { className: "flex gap-3", children: [_jsx("div", { children: "Cumulative" }), _jsx("div", { children: "Flat" }), _jsx("div", { children: "Source" })] })] }), _jsx("div", { className: "text-xs", children: _jsx(SyntaxHighlighter, { language: language, style: isDarkMode ? atomOneDark : atomOneLight, showLineNumbers: true, renderer: renderer, customStyle: { padding: 0, height: '90vh' }, children: content }) })] }));
};
